import NextImage from "next/image";
import NextLink from "next/link";

import { Button } from "@jetblue/kit/button";
import type { ButtonProperties } from "@jetblue/kit/button";
import { IcMosaicStatus } from "@jetblue/kit-icons/ic-mosaic-status";
import type { EnhancedUserData } from "@jetblue/dotcom-auth";

export type TrueBlueUserProperties = {
  customerDetails: EnhancedUserData;
  loyalty: {
    cta: {
      href: string;
      isExternal: boolean;
      label: string;
      target?: "_blank" | "_self";
      variant?: ButtonProperties["variant"];
    };
    image?: string;
    imageAlt?: string;
    imageSrc?: unknown;
    labels: {
      greeting: string;
      membershipNumber: string;
      mosaic: string;
      points: string;
      pts: string;
      tiles: string;
      travelCredits: string;
    };
    mosaic: {
      theme: "dark" | "light";
      variant?: ButtonProperties["variant"];
    };
    theme: "dark" | "light";
  };
  isMosaic: boolean;
};
export const TrueBlueUser = ({
  customerDetails,
  isMosaic,
  loyalty,
}: TrueBlueUserProperties) => {
  const mosaicTier = customerDetails.loyaltyDetails.membershipTier?.replaceAll(
    /\D+/g,
    "",
  );
  const loyaltyCTA = loyalty.cta.variant ?? "standard";
  const mosaicCTA = loyalty.mosaic.variant ?? "primary";

  const amount = customerDetails.travelBankDetails?.balance?.amount;

  return (
    <section
      className="
        bg-background-surface-variant-low p-6 text-foreground-primary-variant

        data-[theme=dark]:bg-background-primary-variant
        data-[theme=dark]:text-foreground-on-primary
      "
      data-fs-element="user-trueblue-details-section"
      data-theme={isMosaic ? loyalty.mosaic.theme : loyalty.theme}
    >
      <div>
        <>
          {isMosaic ? (
            <div className="mb-3 flex items-center justify-center">
              <IcMosaicStatus aria-hidden className="me-2 w-6" />
              <span className="text-caption">
                {loyalty.labels.mosaic} {mosaicTier}
              </span>
            </div>
          ) : (
            <>
              {loyalty.imageAlt && loyalty.image && (
                <div className="mb-3 flex h-6 items-center justify-center">
                  <NextImage
                    alt={loyalty.imageAlt}
                    height={8}
                    src={loyalty.image}
                    width={75}
                  />
                </div>
              )}
            </>
          )}
        </>

        <div className="mb-2 text-center text-body font-extrabold">
          {loyalty.labels.greeting}&nbsp;
          {customerDetails.firstName}
        </div>

        <div className="mb-3 text-center text-caption">
          {loyalty.labels.membershipNumber}
          {customerDetails.loyaltyDetails.membershipID}
        </div>
      </div>

      <div
        className="
          mb-4 flex justify-evenly text-foreground-on-surface

          data-[is-mosaic=true]:text-foreground-on-primary
        "
        data-is-mosaic={isMosaic}
      >
        <div className="text-center">
          <div className="text-caption">{loyalty.labels.tiles}</div>
          <div className="text-body font-extrabold">
            {customerDetails.loyaltyDetails.tiles ?? 0}
          </div>
        </div>

        <div
          className="
            mt-1 inline-block h-9 w-px
            bg-background-surface-container-variant-medium
          "
        />

        <div className="text-center">
          <div className="text-caption">{loyalty.labels.points}</div>
          <div className="text-body font-extrabold">
            {customerDetails.loyaltyDetails.accountBalance?.toLocaleString(
              "en",
            ) ?? 0}
          </div>
        </div>

        {Boolean(amount) && (
          <>
            <div
              className="
                mt-1 inline-block h-9 w-px
                bg-background-surface-container-variant-medium
              "
            />

            <div className="text-center">
              <div className="text-center text-caption">
                <div className="text-caption">
                  {loyalty.labels.travelCredits}
                </div>

                <div className="text-body font-extrabold">{amount}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {loyalty.cta.href && loyalty.cta.label && (
        <Button
          as={NextLink}
          data-fs-element="user-trueblue-details-cta-link"
          href={loyalty.cta.href}
          isFullWidth
          size="small"
          target={loyalty.cta.target}
          variant={isMosaic ? mosaicCTA : loyaltyCTA}
        >
          {loyalty.cta.label}
        </Button>
      )}
    </section>
  );
};
